import { useTranslation } from 'react-i18next';
import { changeLanguage, getCurrentLanguage, supportedLanguages } from '../i18n';

export interface LanguageOption {
  code: string;
  name: string;
  nativeName: string;
}

export interface UseLanguageReturn {
  // Current language
  currentLanguage: string;
  
  // Supported languages
  languages: LanguageOption[];
  
  // Translation function
  t: (key: string, options?: Record<string, unknown>) => string;
  
  // Change language function
  changeLanguage: (language: string) => void;
  
  // Check if language is supported
  isLanguageSupported: (language: string) => boolean;
  
  // Get language display name
  getLanguageDisplayName: (language: string) => string;
  
  // Check if current language is RTL (for future RTL language support)
  isRTL: boolean;
}

export const useLanguage = (): UseLanguageReturn => {
  const { t } = useTranslation();
  
  const currentLanguage = getCurrentLanguage();
  
  const handleChangeLanguage = (language: string) => {
    if (isLanguageSupported(language)) {
      changeLanguage(language);
    } else {
      console.warn(`Language ${language} is not supported`);
    }
  };
  
  const isLanguageSupported = (language: string): boolean => {
    return supportedLanguages.some(lang => lang.code === language);
  };
  
  const getLanguageDisplayName = (language: string): string => {
    const lang = supportedLanguages.find(l => l.code === language);
    return lang ? lang.nativeName : language;
  };
  
  // Currently no RTL languages supported, but ready for future expansion
  const isRTL = false; // currentLanguage === 'ar' || currentLanguage === 'he';
  
  return {
    currentLanguage,
    languages: supportedLanguages,
    t,
    changeLanguage: handleChangeLanguage,
    isLanguageSupported,
    getLanguageDisplayName,
    isRTL,
  };
};